import StatsInfo from "@components/new-ui/IndustriesSection/StatsInfo";
import NewLayout from "@components/new-ui/Layout";
import React from "react";
import { CasestudyCard, CasestudyCardProps } from ".";
import { CaseStudyHerosection, CaseStudyInformation, FeatureList, Navbar, navLinks, OrganizationProfile } from "./alrannan-administrative-services";

const casestudyCardsDetails: CasestudyCardProps[] = [
    {
        imageUrl: "https://assets.gallabox.com/gb-home/case-study/alrannan-admin-service.png",
        title: "How Alrannan Administrative Services automated sales with Gallabox",
        sector: "Service",
        externalLink: "/case-study/alrannan-administrative-services",
    },
    {
        imageUrl: "https://assets.gallabox.com/gb-home/case-study/goireland.png",
        title: "Success Story : Improved Transparency and Increased Conversions",
        sector: "Education",
        externalLink: "/case-study/goireland",
    },
    {
        imageUrl: "https://assets.gallabox.com/gb-home/case-study/dubai-housekeeping.png",
        title: "Gallabox transforms Dubai Housekeeping's journey to service excellence on WhatsApp",
        sector: "Service",
        externalLink: "/case-study/dubai-housekeeping",
    },
];

const ThreadBucketStudio: React.FC = () => {
    return (
        <NewLayout
            title="Thread Bucket Studio | WhatsApp automation customer story"
            description="Learn how Thread Bucket Studio transformed their retail customer service operations and team collaboration using Gallabox's WhatsApp Business solutions."
            canonical="https://gallabox.com/case-study/thread-bucket-studio"
            ogAttributes={{
                image: "https://assets.gallabox.com/gb-home/case-study/thread-bucket-studio/thread-bucket-+og.png",
            }}
        >
            <CaseStudyHerosection
                title=" Why Thread Bucket Studio chose Gallabox over Wati for seamless team communication"
                description="Learn how Thread Bucket Studio transformed their retail customer service operations and team collaboration using Gallabox's WhatsApp Business solutions."
                imageUrl={"https://assets.gallabox.com/gb-home/case-study/thread-bucket-studio/thread-bucket-studio.png"}
                redirectUrl={"/case-study"}
            />
            <StatsInfo
                statsInfo={[
                    { percentage: "68%", label: "Higher business growth" },
                    { percentage: "50%", label: "Increase in employee productivity" },
                    { percentage: "30%", label: "Growth in customer base" },
                ]}
            />

            <section className="max-w-6xl mx-auto  md:grid md:grid-cols-6 mt-24 mb-24">
                <div className="bg-white col-span-1">
                    <div style={{ position: "sticky", top: "100px" }}>
                        <OrganizationProfile
                            companyProfileData={[
                                { label: "Company", value: ["Thread bucket studio"] },
                                { label: "Industry", value: ["E-commerce"] },
                                { label: "Employees", value: ["2-10 "] },
                                { label: "Team", value: ["Sales", "Support", "Marketing"] },
                            ]}
                            product={{
                                title: "Features Used",
                                productLink: [
                                    { link: "https://gallabox.com/whatsapp-flows", label: "WhatsApp Flows" },
                                    { link: "https://gallabox.com/whatsapp-chatbot", label: "Whatsapp Chatbot" },
                                    { link: "https://gallabox.com/whatsapp-broadcast", label: "WhatsApp Broadcast" },
                                    { link: "https://gallabox.com/whatsapp-integrations", label: "Integrations" },
                                    { link: "https://gallabox.com/whatsapp-shared-inbox", label: "Shared team Inbox" },
                                ],
                            }}
                        />
                    </div>
                </div>
                <div className=" col-span-4 md:pl-8 px-4">
                    <div className="flex flex-col ">
                        <section id="About">
                            <CaseStudyInformation mainTitle={"Thread Bucket Studio's quest for better team oversight"} />
                            <p>
                                <a href="https://www.threadbucketstudio.in/" className="text-blue-500">
                                    Thread Bucket Studio LLP
                                </a>{" "}
                                is a thriving manufacturing and retail business that makes high-quality yet pocket friendly ethnic wear for women. They faced the critical challenge
                                of maintaining transparency and accountability in their customer communications. As their team expanded, they needed a robust system to monitor and
                                manage customer interactions.
                            </p>
                        </section>

                        <section id="Challenges">
                            <CaseStudyInformation
                                subTitle={"The struggle with traditional WhatsApp business solutions"}
                                descriptions={["Before partnering with Gallabox, Thread Bucket Studio encountered several operational challenges:"]}
                                className="gap-6"
                            />
                            <FeatureList
                                List={[
                                    { listItem: "Limited visibility into employee conversations with customers" },
                                    { listItem: "Risk of message deletion affecting accountability" },
                                    { listItem: "Inefficient team communication processes" },
                                    { listItem: "Lack of proper oversight on customer interactions" },
                                ]}
                                className="mt-4 bg-gray-100 mb-6"
                            />
                            <p className="mb-6">
                                &quot;We were unable to see the chats of the employees, and due to the ‘delete’ feature, many of the chats and messages would be deleted, resulting
                                in poor transparency and accountability,&quot; explains Rahul Rawal, Sales and Marketing Manager, from Thread Bucket Studio, highlighting their
                                primary concern.
                            </p>
                            <p className="mb-6">
                                Initially, they tried working with WATI, but service-related issues led them to search for a better alternative. The hunt for a more reliable
                                solution eventually led them to Gallabox.
                            </p>
                        </section>

                        <section id="Solutions">
                            <CaseStudyInformation subTitle={"Why Gallabox stood out from the competition"} className="gap-2" />
                            <p>
                                The decision to switch to Gallabox was driven by one key factor – superior features. As Rahul notes, &quot;From what we have explored and tested,
                                Gallabox’s features are extremely effective when compared to other messaging software in the market.&quot; They found their use cases solved with
                                Gallabox’s purpose-built WhatsApp Business functionalities:
                            </p>

                            <FeatureList
                                List={[
                                    { listItem: "Comprehensive chat monitoring capabilities" },
                                    { listItem: "Shared inbox with team collaboration" },
                                    { listItem: "Gallabox Gen AI for crafting messages and translations" },
                                    { listItem: "Rule-based and AI-powered chatbot builders to achieve business outcomes" },
                                ]}
                                className="mt-5 bg-gray-100 mb-6"
                            />

                            <CaseStudyInformation
                                subTitle={"Transforming operations with Gallabox"}
                                descriptions={["Thread Bucket Studio leveraged multiple Gallabox features to revolutionize their customer communication:"]}
                                className="mt-12 gap-5 mb-4"
                            />
                            <CaseStudyInformation
                                subTitle={<p className="text-xl font-medium">Shared team inbox for better oversight</p>}
                                descriptions={[
                                    "The shared inbox feature enabled supervisors to monitor all customer interactions, ensuring quality service and maintaining accountability across the team.",
                                ]}
                                className="gap-5 mb-4 text-xl"
                            />
                            <CaseStudyInformation
                                subTitle={<p className="text-xl font-medium">Broadcast messages for wider reach</p>}
                                descriptions={[
                                    "Using Gallabox's broadcast capabilities, they could efficiently communicate with their growing customer base, sharing updates and promotional content.",
                                ]}
                                className="gap-5 mb-4"
                            />
                            <CaseStudyInformation
                                subTitle={<p className="text-xl font-medium">Automated workflows with chatbots and flows</p>}
                                descriptions={[
                                    "By implementing chatbots and automated flows, they streamlined routine communications while maintaining personal touch in customer interactions.",
                                ]}
                                className="gap-5 mb-4"
                            />
                            <CaseStudyInformation
                                subTitle={<p className="text-xl font-medium">Seamless integrations for better efficiency</p>}
                                descriptions={[
                                    "The integration capabilities helped them connect their existing tools with WhatsApp communication, creating a more efficient workflow.",
                                ]}
                                className="gap-5 mb-4"
                            />
                        </section>

                        <section id="Impact">
                            <CaseStudyInformation
                                subTitle={"The numbers speak for themselves"}
                                descriptions={["The implementation of Gallabox brought significant improvements to Thread Bucket Studio's operations:"]}
                            />
                            <p className="text-xl font-medium mt-6">50% boost in employee productivity</p>
                            <FeatureList
                                List={[
                                    { listItem: "Streamlined communication processes" },
                                    { listItem: "Better task management and ticket deflection" },
                                    { listItem: "Improved team collaboration." },
                                ]}
                                className="mb-12 mt-4 bg-gray-100"
                            />
                            <p className="text-xl font-medium mt-6">30% growth in customer bas</p>
                            <FeatureList
                                List={[
                                    { listItem: "Enhanced customer engagement with automation" },
                                    { listItem: "Better service delivery through bots and flows" },
                                    { listItem: "Improved customer satisfaction with faster resolutions." },
                                ]}
                                className="mb-12 mt-4 bg-gray-100"
                            />
                            <p className="text-xl font-medium mt-6">20% reduction in marketing spend</p>
                            <FeatureList
                                List={[
                                    { listItem: "More efficient marketing with broadcast templates " },
                                    { listItem: "Better return on ad spends with Click-to-WhatsApp ads" },
                                    { listItem: "Improved ROI on overall marketing efforts." },
                                ]}
                                className="mb-12 mt-4 bg-gray-100"
                            />
                            <CaseStudyInformation
                                subTitle={"Thread Bucket Studio’s story is a resounding success of conversational commerce through WhatsApp"}
                                descriptions={["With Gallabox as their WhatsApp Business API partner, Thread Bucket Studio continues to:"]}
                                className="gap-5 mb-4"
                            />
                            <FeatureList
                                List={[
                                    { listItem: "Maintain transparency in team communications" },
                                    { listItem: "Improve customer service efficiency" },
                                    { listItem: "Scale their operations effectively" },
                                    { listItem: "Reduce operational costs" },
                                    { listItem: "Enhance team productivity" },
                                ]}
                                className="mb-4"
                            />
                            <p className="mb-8">
                                Thread Bucket Studio has not just improved their team communication but has transformed their entire business operations through effective use of
                                WhatsApp Business solutions.
                            </p>
                        </section>
                    </div>
                </div>
                <div>
                    <div style={{ position: "sticky", top: "100px", minHeight: "510px" }} className="hidden md:block">
                        <Navbar links={navLinks} />
                    </div>
                </div>
            </section>
            <div className="max-w-6xl mx-auto text-center md:text-left">
                <div>
                    <h3 className="text-gray-900 max-w-6xl mx-auto mb-10 mt-5 text-center md:text-left">Keep exploring stories like this.</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 max-w-6xl mx-auto gap-10 mb-20">
                        {casestudyCardsDetails?.map((data, index) => (
                            <CasestudyCard key={index} {...data} />
                        ))}
                    </div>
                </div>
            </div>
        </NewLayout>
    );
};

export default ThreadBucketStudio;
